import { type DatasetSummaryGoalState } from "../buildRequestTemplate";

import styles from "./DatasetSummaryGoalForm.module.css";
import { ImplicitDatasetChip } from "./ImplicitDatasetChip";
import { type SummaryGoalErrors } from "./validation";

interface Props {
  goal: DatasetSummaryGoalState;
  errors: SummaryGoalErrors;
  onChange: (next: DatasetSummaryGoalState) => void;
}

/**
 * First-class form for ``DatasetSummaryAgentGoal``.
 *
 * The only operator-facing field is the optional summary-format hint:
 * a freeform natural-language nudge about the shape of the summary
 * (bullets vs prose, what to emphasize). Leaving it blank lets the
 * server-side goal handler use its opinionated default, which is
 * usually the right choice.
 */
export function DatasetSummaryGoalForm({ goal, errors, onChange }: Props) {
  const fieldId = "summary-format-hint";
  const helpId = `${fieldId}-help`;
  const errorId = `${fieldId}-error`;
  const hasErrors = errors.summary_format_spec_prompt.length > 0;
  const describedBy = hasErrors ? `${helpId} ${errorId}` : helpId;
  return (
    <div className={styles.container}>
      <ImplicitDatasetChip />
      <div className={styles.field}>
        <label className={styles.label} htmlFor={fieldId}>
          Summary format hint
          <span className={styles.optional}>(optional)</span>
        </label>
        <p id={helpId} className={styles.help}>
          Natural-language guidance on how to structure the summary &mdash; e.g.
          &ldquo;bullet points, focus on anomalies.&rdquo; Leave blank for the
          default format. You can reference variables with{" "}
          <code>{"{{name}}"}</code>.
        </p>
        <textarea
          id={fieldId}
          className={styles.textarea}
          rows={4}
          value={goal.summary_format_spec_prompt}
          onChange={(e) =>
            onChange({ ...goal, summary_format_spec_prompt: e.target.value })
          }
          placeholder="Bullet points, emphasize anomalies and root causes."
          aria-invalid={hasErrors}
          aria-describedby={describedBy}
        />
        {hasErrors && (
          <div id={errorId} role="alert">
            {errors.summary_format_spec_prompt.map((message) => (
              <div key={message} className={styles.error}>
                {message}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
